import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useApps } from "@theg1group/ui";
import { DEFAULT_COMMANDS } from "./commands.js";
import { TYPE_LABELS } from "./types.js";
import { useDebounced } from "./useDebounced.js";
import { useUniversalLauncher } from "./useUniversalLauncher.js";
import { getRecent, recordRecent } from "./recent.js";
const DEFAULT_ENDPOINT = "https://search-api.g1.network";
const sx = {
    backdrop: {
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "10vh",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
    },
    panel: {
        width: "100%",
        maxWidth: "42rem",
        overflow: "hidden",
        borderRadius: "0.5rem",
        border: "1px solid rgba(255,255,255,0.1)",
        background: "#18181b",
        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.6)",
    },
    inputWrap: { borderBottom: "1px solid rgba(255,255,255,0.1)", padding: "0.75rem 1rem" },
    input: {
        width: "100%",
        background: "transparent",
        fontSize: "1rem",
        color: "#fff",
        outline: "none",
        border: "none",
    },
    results: { maxHeight: "60vh", overflowY: "auto" },
    rowMuted: { padding: "0.75rem 1rem", fontSize: "0.875rem", color: "#a1a1aa" },
    rowError: { padding: "0.75rem 1rem", fontSize: "0.875rem", color: "#f87171" },
    rowEmpty: { padding: "1.5rem 1rem", fontSize: "0.875rem", color: "#a1a1aa" },
    rowHint: { padding: "1.5rem 1rem", fontSize: "0.875rem", color: "#71717a" },
    groupSection: { borderBottom: "1px solid rgba(255,255,255,0.05)" },
    groupHeader: {
        background: "rgba(255,255,255,0.05)",
        padding: "0.375rem 1rem",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#a1a1aa",
    },
    groupList: { listStyle: "none", margin: 0, padding: 0 },
    hitButton: {
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
        width: "100%",
        padding: "0.5rem 1rem",
        textAlign: "left",
        background: "transparent",
        border: "none",
        color: "inherit",
        cursor: "pointer",
    },
    hitTitle: { fontSize: "0.875rem", color: "#fff" },
    hitSubtitle: { fontSize: "0.75rem", color: "#a1a1aa" },
    appIcon: { color: "#fff", flexShrink: 0, display: "inline-flex", alignItems: "center" },
};
function fuzzyMatch(needle, haystack) {
    if (!needle)
        return true;
    return haystack.toLowerCase().includes(needle.toLowerCase());
}
function filterApps(apps, query, appAccess, currentAppId) {
    return apps.filter((app) => {
        if (currentAppId && app.id === currentAppId)
            return false;
        if (appAccess && appAccess.length > 0 && !appAccess.includes("*") && !appAccess.includes(app.id)) {
            return false;
        }
        return fuzzyMatch(query, `${app.name} ${app.id}`);
    });
}
function sortByRecent(apps, recentIds) {
    if (recentIds.length === 0)
        return apps;
    const rank = new Map(recentIds.map((id, i) => [id, i]));
    return [...apps].sort((a, b) => {
        const ra = rank.get(a.id) ?? Number.POSITIVE_INFINITY;
        const rb = rank.get(b.id) ?? Number.POSITIVE_INFINITY;
        return ra - rb;
    });
}
/** True when the viewport is phone-width (≤ 640px). Reactive to rotation/resize. */
function useIsNarrow() {
    const [narrow, setNarrow] = useState(() => typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)").matches : false);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const mq = window.matchMedia("(max-width: 640px)");
        const onChange = () => setNarrow(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return narrow;
}
export const UniversalLauncher = ({ endpoint = DEFAULT_ENDPOINT, types, limit = 10, appAccess, currentAppId, commands, }) => {
    const { open, closeLauncher, initialQuery } = useUniversalLauncher();
    const [query, setQuery] = useState(initialQuery);
    const debounced = useDebounced(query, 150);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [response, setResponse] = useState(null);
    const [recentIds, setRecentIds] = useState([]);
    const isNarrow = useIsNarrow();
    const apps = useApps();
    useEffect(() => {
        if (!open) {
            setQuery("");
            setResponse(null);
            setError(null);
            return;
        }
        setRecentIds(getRecent());
    }, [open]);
    // Mirror open + query into the URL as `?g=<query>`. replaceState avoids a
    // history entry per keystroke. Empty query while open → `?g=`. Closed → no param.
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        try {
            const url = new URL(window.location.href);
            if (open) {
                url.searchParams.set("g", query);
            }
            else {
                url.searchParams.delete("g");
            }
            window.history.replaceState(null, "", url.toString());
        }
        catch {
            // noop — non-standard URL or sandboxed iframe
        }
    }, [open, query]);
    const isCommandMode = query.startsWith(">");
    const commandQuery = isCommandMode ? query.slice(1).trim() : "";
    useEffect(() => {
        if (!open || !debounced.trim() || isCommandMode) {
            setResponse(null);
            return;
        }
        const ctrl = new AbortController();
        setLoading(true);
        setError(null);
        const url = new URL("/v1/search", endpoint);
        url.searchParams.set("q", debounced);
        url.searchParams.set("limit", String(limit));
        if (types && types.length > 0)
            url.searchParams.set("types", types.join(","));
        fetch(url.toString(), { credentials: "include", signal: ctrl.signal })
            .then(async (res) => {
            if (!res.ok)
                throw new Error(`search ${res.status}`);
            const data = (await res.json());
            setResponse(data);
        })
            .catch((err) => {
            if (err.name === "AbortError")
                return;
            setError(err instanceof Error ? err.message : String(err));
        })
            .finally(() => setLoading(false));
        return () => ctrl.abort();
    }, [open, debounced, endpoint, limit, types, isCommandMode]);
    const visibleApps = useMemo(() => {
        if (isCommandMode)
            return [];
        const filtered = filterApps(apps, query, appAccess, currentAppId);
        return sortByRecent(filtered, recentIds);
    }, [isCommandMode, apps, query, appAccess, currentAppId, recentIds]);
    const visibleCommands = useMemo(() => {
        if (!isCommandMode)
            return [];
        const all = [...DEFAULT_COMMANDS, ...(commands ?? [])];
        if (!commandQuery)
            return all;
        const q = commandQuery.toLowerCase();
        return all.filter((c) => `${c.label} ${c.id} ${c.hint ?? ""}`.toLowerCase().includes(q));
    }, [isCommandMode, commandQuery, commands]);
    if (!open)
        return null;
    const onCommandRun = async (cmd) => {
        closeLauncher();
        try {
            await cmd.action();
        }
        catch (err) {
            console.error(`launcher command ${cmd.id} failed`, err);
        }
    };
    const onAppLaunch = (app) => {
        recordRecent(app.id);
        closeLauncher();
        window.location.href = app.url;
    };
    // On phones (no Cmd/Ctrl+G) the launcher is opened via the shell's search
    // button; render it as a full-bleed sheet with a 16px input (avoids iOS
    // zoom-on-focus) and a results area that fills the screen.
    const backdropStyle = isNarrow
        ? { ...sx.backdrop, paddingTop: 0, alignItems: "stretch" }
        : sx.backdrop;
    const panelStyle = isNarrow
        ? {
            ...sx.panel,
            maxWidth: "none",
            height: "100%",
            borderRadius: 0,
            border: "none",
            display: "flex",
            flexDirection: "column",
        }
        : sx.panel;
    const inputStyle = isNarrow ? { ...sx.input, fontSize: "16px" } : sx.input;
    const resultsStyle = isNarrow ? { ...sx.results, maxHeight: "none", flex: 1 } : sx.results;
    return (_jsx("div", { style: backdropStyle, onClick: closeLauncher, onKeyDown: (e) => e.key === "Escape" && closeLauncher(), role: "dialog", "aria-modal": "true", children: _jsxs("div", { style: panelStyle, onClick: (e) => e.stopPropagation(), onKeyDown: (e) => e.stopPropagation(), children: [_jsx("div", { style: sx.inputWrap, children: _jsx("input", { type: "text", autoFocus: true, value: query, onChange: (e) => setQuery(e.target.value), placeholder: "Apps \u00B7 Search \u00B7 Recent \u00B7 > Commands", style: inputStyle }) }), _jsx("div", { style: resultsStyle, children: isCommandMode ? (visibleCommands.length > 0 ? (_jsx(CommandGroup, { commands: visibleCommands, onSelect: onCommandRun })) : (_jsx("div", { style: sx.rowEmpty, children: "\u041A\u043E\u043C\u0430\u043D\u0434\u0430 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430. \u0412\u0432\u0435\u0434\u0438\u0442\u0435 `>` \u0431\u0435\u0437 \u0430\u0440\u0433\u0443\u043C\u0435\u043D\u0442\u043E\u0432 \u0447\u0442\u043E\u0431\u044B \u0443\u0432\u0438\u0434\u0435\u0442\u044C \u0432\u0441\u0435." }))) : (_jsxs(_Fragment, { children: [visibleApps.length > 0 && (_jsx(AppGroup, { apps: visibleApps, onSelect: onAppLaunch })), loading && _jsx("div", { style: sx.rowMuted, children: "\u041F\u043E\u0438\u0441\u043A\u2026" }), error && _jsxs("div", { style: sx.rowError, children: ["\u041E\u0448\u0438\u0431\u043A\u0430: ", error] }), !loading && !error && debounced && response && response.total === 0 && (_jsx("div", { style: sx.rowEmpty, children: "\u041D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E \u0432 \u0434\u0430\u043D\u043D\u044B\u0445" })), response && response.total > 0 && (_jsx(ResultGroups, { groups: response.groups, onSelect: closeLauncher })), !response && !loading && !error && !query && visibleApps.length === 0 && (_jsx("div", { style: sx.rowHint, children: "Cmd/Ctrl+G \u2014 \u043F\u0435\u0440\u0435\u043A\u043B\u044E\u0447\u0438\u0442\u044C. `>` \u2014 \u043A\u043E\u043C\u0430\u043D\u0434\u044B. Esc \u2014 \u0437\u0430\u043A\u0440\u044B\u0442\u044C." }))] })) })] }) }));
};
const CommandGroup = ({ commands, onSelect }) => (_jsxs("div", { style: sx.groupSection, children: [_jsx("div", { style: sx.groupHeader, children: "Commands" }), _jsx("ul", { style: sx.groupList, children: commands.map((cmd) => (_jsx("li", { children: _jsxs("button", { type: "button", style: sx.hitButton, onMouseEnter: (e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    }, onMouseLeave: (e) => {
                        e.currentTarget.style.background = "transparent";
                    }, onClick: () => onSelect(cmd), children: [_jsx("span", { style: sx.hitTitle, children: cmd.label }), cmd.hint && (_jsx("span", { style: { ...sx.hitSubtitle, marginLeft: "auto" }, children: cmd.hint }))] }) }, cmd.id))) })] }));
const AppGroup = ({ apps, onSelect }) => (_jsxs("div", { style: sx.groupSection, children: [_jsx("div", { style: sx.groupHeader, children: "Apps" }), _jsx("ul", { style: sx.groupList, children: apps.map((app) => (_jsx("li", { children: _jsxs("button", { type: "button", style: sx.hitButton, onMouseEnter: (e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    }, onMouseLeave: (e) => {
                        e.currentTarget.style.background = "transparent";
                    }, onClick: () => onSelect(app), children: [_jsx("span", { style: sx.appIcon, children: _jsx(app.Icon, { size: 16 }) }), _jsx("span", { style: sx.hitTitle, children: app.name }), _jsx("span", { style: { ...sx.hitSubtitle, marginLeft: "auto" }, children: app.url.replace("https://", "") })] }) }, app.id))) })] }));
const ResultGroups = ({ groups, onSelect }) => {
    const types = Object.keys(groups);
    return (_jsx("div", { children: types.map((t) => (_jsxs("div", { style: sx.groupSection, children: [_jsx("div", { style: sx.groupHeader, children: TYPE_LABELS[t] ?? t }), _jsx("ul", { style: sx.groupList, children: groups[t]?.map((hit) => (_jsx("li", { children: _jsx("button", { type: "button", style: sx.hitButton, onMouseEnter: (e) => {
                                e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                            }, onMouseLeave: (e) => {
                                e.currentTarget.style.background = "transparent";
                            }, onClick: () => {
                                window.location.href = hit.deep_link;
                                onSelect();
                            }, children: _jsxs("div", { children: [_jsx("div", { style: sx.hitTitle, children: hit.title }), hit.subtitle && _jsx("div", { style: sx.hitSubtitle, children: hit.subtitle })] }) }) }, hit.id))) })] }, t))) }));
};
//# sourceMappingURL=UniversalLauncher.js.map